import { useCallback, useEffect, useId, useRef, useState } from 'react'
import type { FormEvent, JSX, KeyboardEvent as ReactKeyboardEvent } from 'react'

import {
  MIRA_DRAFT_CHANNELS,
  MIRA_DRAFT_OCCASIONS,
  type AiTask,
  type MiraDraftChannel,
  type MiraDraftOccasion,
  type MiraImage,
} from '@lensmira/domain'
import type { MessageKey } from '@lensmira/i18n'
import type { GridState } from '@lensmira/ui'

import { Ltr, useTranslation } from '../i18n.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { sentence } from '../sentence.js'
import { rxGridOf, type ComposedAnswer } from './acceptance.js'
import { browserImageTools, type MiraImageTools } from './browser-images.js'
import { NotCropped, imagePayload } from './images.js'
import { MiraAcceptance } from './MiraAcceptance.js'
import { MiraImages } from './MiraImages.js'
import {
  mapAskOutcome,
  type AskOutcome,
  type DegradedReason,
  type MiraQuota,
  type MiraProposal,
} from './wire.js'

/**
 * Mira, as a surface rather than a page. `AI-022`, `AI-001`, `AI-005`, `AI-006`.
 *
 * > `AI-022` `MUST`: Mira is a consistent surface across the product, a keyboard shortcut and an
 * > input that understands the current context (this customer, this job, this report), not a
 * > separate page.
 *
 * **Ctrl+K from anywhere, and Escape puts you back where you were.** The element that had focus is
 * remembered when Mira opens and given it back when she closes, because the counter is keyboard
 * first (`KB-02`) and a dispenser halfway through a prescription grid must not lose their cell to
 * a question about a report. The shortcut is matched on `event.code`, not `event.key`: on an Urdu
 * keyboard layout `key` is an Urdu letter and a shortcut keyed on "k" silently stops working for
 * exactly the shops this ships to. Ctrl+D is the grid's (copy OD to OS), so Ctrl+K does not collide.
 *
 * **It is a region in the page, not a dialog.** No backdrop, no `aria-modal`, no focus trap: the
 * rail, the queue badge and the screen underneath stay reachable by Tab while she is open, because a
 * modal on a till is a modal in front of a customer. Her state survives closing, so a proposal that
 * is half decided is still there when the panel is reopened.
 *
 * **The context is displayed and sent as a pointer, never as content.** What the person is looking
 * at goes to the API as `targetType` and `targetId`, which are stored on the proposal and never
 * reach a provider. The payload is only what they typed. `AI-004` refuses a payload carrying a name
 * or identifier at any depth (`assertPayloadMinimised`), so a context built into the payload would
 * be refused, and one that got past would be a customer's identity sent to a sub-processor.
 *
 * **Asking and answering are both writes, so both go through the queue** (`OFF-001a`).
 *
 * **Offline, she says so and queues nothing** (`AI-005`). A request that sits on the device and is
 * sent when the signal returns would spend a credit, hours later, on a question nobody is waiting on.
 * One already queued but not yet sent when the connection drops is taken back off the queue.
 *
 * **Exhaustion and "not set up" are answers, not failures** (`AI-006`, `AI-005`). Both leave the
 * manual path in view and say so in a sentence; neither is rendered as an error.
 */

export type MiraContext =
  | { readonly kind: 'customer'; readonly id: string; readonly name: string }
  | { readonly kind: 'order'; readonly id: string; readonly number: string | null }
  | { readonly kind: 'job'; readonly id: string; readonly number: string }
  | { readonly kind: 'section'; readonly label: string }

export interface MiraSurfaceProps {
  readonly branchId: string
  readonly context: MiraContext
  /** A read, so it comes from `api/reads.ts` and not the queue. Called when she is opened. */
  readonly loadQuota: () => Promise<readonly MiraQuota[]>
  /**
   * Hand accepted prescription values to the ordinary capture screen. Absent when this person may
   * not create a prescription or there is no customer to put one against, and the button with it.
   * It prefills the grid; the person still saves it through the screen's own write.
   */
  readonly onTakeRx?: (grid: GridState) => void
  /**
   * How a photograph is previewed and cropped. Defaults to the real browser.
   *
   * Injected because jsdom has no image decoding, no canvas and no `URL.createObjectURL`, so the
   * real implementation cannot run in a suite. `browser-images.ts` argues the seam and says what
   * it costs.
   */
  readonly imageTools?: MiraImageTools
}

/**
 * Every task the counter can ask, and whether asking it means sending photographs.
 *
 * The three vision tasks are here now that there is an image payload contract to build to
 * (`packages/domain/src/mira-payloads.ts`). They were absent for exactly the reason this comment
 * used to give: a button that cannot build a payload is a door that opens onto nothing, and
 * `AI-010` to `AI-012` sat built and unreachable through two releases because of it.
 *
 * `frame_match` (`AI-016`) is still absent and stays absent: it has no prompt and no route, so
 * every ask would walk an empty chain and report Mira as not set up for this shop.
 */
const TASKS: readonly {
  readonly task: AiTask
  readonly label: MessageKey
  /** Present and true where the payload is photographs. */
  readonly images?: true
}[] = [
  { task: 'help', label: 'mira.task.help' },
  { task: 'report_query', label: 'mira.task.reportQuery' },
  { task: 'message_draft', label: 'mira.task.messageDraft' },
  { task: 'rx_ocr', label: 'mira.task.rxOcr', images: true },
  { task: 'catalogue_capture', label: 'mira.task.catalogueCapture', images: true },
  { task: 'price_sheet_parse', label: 'mira.task.priceSheetParse', images: true },
]

/**
 * Whether this task is asked with photographs.
 *
 * Read off the table above rather than listed again, because a task offered with no image input is
 * a decline at every rank and a task with an image input and no entry here cannot be asked at all.
 * `AI_VISION_TASKS` in the domain is the wider set and includes `frame_match`, which has no prompt.
 */
const TAKES_IMAGES = (task: AiTask): boolean =>
  TASKS.some((entry) => entry.task === task && entry.images === true)

/**
 * What each task actually needs in its payload.
 *
 * All three used to send `{ question }`, which only `help` takes. `report_query` needs today's date
 * and `message_draft` needs an occasion and a channel, so two of the three tasks on offer declined
 * at rank 1, declined again at rank 2, and reported themselves as "Mira is not set up for this
 * shop yet" forever. Nothing crossed the two sides to catch it: the API suite drives stub providers
 * and these tests drive a fake transport.
 *
 * `today` is deliberately not here. The server fills it from the branch's own calendar (Appendix J),
 * because a tablet with a wrong clock would otherwise ask about the wrong week.
 */
const DRAFT_OCCASION_LABELS: Readonly<Record<MiraDraftOccasion, MessageKey>> = {
  order_ready: 'mira.occasion.orderReady',
  balance_due: 'mira.occasion.balanceDue',
  recall: 'mira.occasion.recall',
  apology: 'mira.occasion.apology',
}

const DRAFT_CHANNEL_LABELS: Readonly<Record<MiraDraftChannel, MessageKey>> = {
  whatsapp: 'mira.channel.whatsapp',
  sms: 'mira.channel.sms',
  email: 'mira.channel.email',
}

/** Whether the task is asked with words. `message_draft` is asked with two choices instead. */
const ASKS_A_QUESTION = (task: AiTask): boolean => task === 'help' || task === 'report_query'

/**
 * What the person is looking at, as one comparable string.
 *
 * Used to tell whether the proposal on screen was asked about the customer, order or job in hand
 * now. See `takeRx`: a decided prescription must not follow the panel to the next person.
 */
const contextKeyOf = (context: MiraContext): string =>
  context.kind === 'section' ? `section:${context.label}` : `${context.kind}:${context.id}`

const REASON_KEYS: Readonly<Record<DegradedReason, MessageKey>> = {
  degraded: 'mira.reason.degraded',
  unavailable: 'mira.reason.unavailable',
  not_allowed: 'mira.reason.notAllowed',
  unquoted: 'mira.reason.unquoted',
}

interface Answered {
  readonly writeId: string
  readonly proposal: MiraProposal
  readonly answer: ComposedAnswer
}

export function MiraSurface({
  branchId,
  context,
  loadQuota,
  onTakeRx,
  imageTools = browserImageTools,
}: MiraSurfaceProps): JSX.Element {
  const { t, locale } = useTranslation()
  const { enqueue, discard, snapshot } = useWriteQueue()
  const regionId = useId()

  const [open, setOpen] = useState(false)
  const [task, setTask] = useState<AiTask>('help')
  const [question, setQuestion] = useState('')
  const [occasion, setOccasion] = useState<MiraDraftOccasion>('order_ready')
  const [channel, setChannel] = useState<MiraDraftChannel>('whatsapp')
  /**
   * The cropped photographs this ask will carry (`AI-004`).
   *
   * Every one was minted by `confirmCrop`, which is what makes `imagePayload` able to refuse an
   * image nobody chose a region of. They are cleared when the task changes: a crop confirmed
   * against a prescription slip must not ride into a request about a supplier's price list.
   */
  const [images, setImages] = useState<readonly MiraImage[]>([])
  /**
   * The context the proposal on screen was asked about, or null before anything was asked.
   *
   * `AI-001`: a value a person confirmed for one customer must not reach another's record. The
   * panel's state deliberately survives closing, so without this the done block and its "open
   * prescription entry with these values" button outlived a change of customer, and the button
   * calls whatever `onTakeRx` the shell passes **now**, which opens the grid for whoever is in hand
   * now. App.tsx's own comment names that failure, an order with one person's eyes and no name on
   * it, as something it had already fixed once.
   */
  const [askedUnder, setAskedUnder] = useState<string | null>(null)
  const [quotas, setQuotas] = useState<readonly MiraQuota[] | null>(null)
  const [quotaFailed, setQuotaFailed] = useState(false)
  const [askWriteId, setAskWriteId] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<AskOutcome | null>(null)
  const [unreadable, setUnreadable] = useState(false)
  const [storageFailed, setStorageFailed] = useState(false)
  const [imagesRefused, setImagesRefused] = useState(false)
  const [cancelled, setCancelled] = useState(false)
  const [answered, setAnswered] = useState<Answered | null>(null)
  const [recorded, setRecorded] = useState(false)

  const opener = useRef<HTMLElement | null>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const occasionRef = useRef<HTMLSelectElement>(null)

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false)
    const target = opener.current
    opener.current = null
    // `isConnected`: the screen that had focus may have been replaced while Mira was open, and
    // focusing a detached node does nothing and loses the focus entirely.
    if (restoreFocus && target?.isConnected === true) target.focus()
  }, [])

  const openPanel = useCallback(() => {
    const active = document.activeElement
    opener.current = active instanceof HTMLElement && active !== document.body ? active : null
    setOpen(true)
  }, [])

  const toggle = useRef<() => void>(() => undefined)
  useEffect(() => {
    toggle.current = () => {
      if (open) close(true)
      else openPanel()
    }
  })

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.isComposing) return
      if (event.code !== 'KeyK' || !(event.ctrlKey || event.metaKey)) return
      if (event.altKey || event.shiftKey) return
      // The browser's own Ctrl+K is "focus the search bar" in some of them.
      event.preventDefault()
      toggle.current()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  // Focus goes into the panel on open: the question if she is waiting to be asked, the heading if
  // there is already a proposal to decide, so a keyboard user lands at the top of what needs them.
  useEffect(() => {
    if (!open) return
    if (outcome?.proposal !== undefined) headingRef.current?.focus()
    else if (inputRef.current !== null) inputRef.current.focus()
    // `message_draft` has no question field, so the first thing that needs the person is the
    // occasion. Without this a keyboard user opened the panel with focus nowhere. A vision task has
    // neither, and a file input is not where a keyboard user wants to land, so focus goes to the
    // heading and Tab walks the task select, the file input and the crop in that order.
    else if (occasionRef.current !== null) occasionRef.current.focus()
    else headingRef.current?.focus()
    // Only on open and close. Re-running as the outcome arrives would pull focus off a field the
    // person had moved to.
  }, [open])

  // `AI-006`'s visible balance, read when she opens and not when the app does.
  useEffect(() => {
    if (!open) return
    let stale = false
    setQuotas(null)
    setQuotaFailed(false)
    loadQuota()
      .then((loaded) => {
        if (!stale) setQuotas(loaded)
      })
      .catch(() => {
        if (!stale) setQuotaFailed(true)
      })
    return () => {
      stale = true
    }
  }, [open, loadQuota])

  const askWrite = snapshot.writes.find((write) => write.id === askWriteId)
  const askBlocked = askWrite?.status === 'blocked'
  const waiting = askWriteId !== null && outcome === null && !unreadable && !askBlocked

  useWriteSettled(snapshot, askWriteId, (write) => {
    try {
      const mapped = mapAskOutcome(write.response)
      setOutcome(mapped)
      // The balance after this call, so the number on screen is the server's and not a subtraction.
      setQuotas((previous) =>
        previous === null
          ? previous
          : [...previous.filter((q) => q.task !== mapped.quota.task), mapped.quota],
      )
    } catch {
      setUnreadable(true)
    }
  })

  useWriteSettled(snapshot, answered?.writeId, () => {
    setRecorded(true)
  })

  const stopWaiting = useCallback(
    (byPerson: boolean) => {
      if (askWriteId === null) return
      const write = snapshot.writes.find((candidate) => candidate.id === askWriteId)
      // A write in flight is already being served and taking it back would lie about a credit that
      // is being spent, so `sending` and `sent` are left alone.
      //
      // **A `blocked` one is the opposite and was not discardable at all.** The server refused it,
      // nothing was billed, and it then sat at the head of a strictly serial queue: `#nextSendable`
      // stops at a blocked write so a payment cannot overtake the sale it belongs to, so a refused
      // Mira ask held every later sale on that device, with its four photographs' worth of body
      // still in IndexedDB and `#expireSent` never selecting it. This is the only way a shopkeeper
      // has to clear it.
      if (write?.status !== 'pending' && write?.status !== 'blocked') return
      void discard(askWriteId)
      setAskWriteId(null)
      if (byPerson) setCancelled(true)
    },
    [askWriteId, discard, snapshot.writes],
  )

  useEffect(() => {
    if (waiting && !snapshot.online) stopWaiting(false)
  }, [waiting, snapshot.online, stopWaiting])

  const quota = quotas?.find((candidate) => candidate.task === task)
  const closedByQuota = quota?.state === 'exhausted' || quota?.state === 'unquoted'
  // `message_draft` is asked with two selects and no words, so an empty question does not block
  // it; a vision task is asked with at least one cropped photograph and nothing else.
  const asked = ASKS_A_QUESTION(task)
    ? question.trim() !== ''
    : TAKES_IMAGES(task)
      ? images.length > 0
      : true
  // `outcome === null` alone left Ask live in two states that cost money or hold the queue. An
  // unreadable answer is a call the server made and billed, with the balance on screen still the one
  // read when the panel opened, so each re-press was another vision call and another upload against
  // a number that never moved (`AI-006` asks for a visible remaining balance). A blocked ask is
  // sitting at the head of the queue, so a re-press enqueues a second copy of the same photographs
  // behind the first. Both need the panel reset, which is what `mira.manual` points at.
  const canAsk =
    snapshot.online &&
    !waiting &&
    !closedByQuota &&
    asked &&
    outcome === null &&
    !unreadable &&
    !askBlocked &&
    !storageFailed

  const target =
    context.kind === 'section' ? {} : { targetType: context.kind, targetId: context.id }
  const contextKey = contextKeyOf(context)

  /**
   * The payload for the task in hand, and nothing else in it.
   *
   * `AI-004` refuses a payload carrying a name or an identifier at any depth, so every field here is
   * either what the person typed or one of a fixed set of words. The context goes to the API as
   * `targetType` and `targetId`, which never reach a provider.
   */
  const payloadFor = (): Record<string, unknown> =>
    TAKES_IMAGES(task)
      ? imagePayload(images)
      : task === 'message_draft'
        ? { occasion, channel }
        : { question: question.trim() }

  const ask = async (): Promise<void> => {
    if (!canAsk) return
    let payload: Record<string, unknown>
    try {
      payload = payloadFor()
    } catch (error) {
      // `imagePayload` throwing is a bug in this component, not something a shopkeeper did: the
      // button is inert without a cropped photograph. It is caught anyway, because an uncaught
      // rejection inside an event handler leaves the panel saying "Asking Mira" forever, and a
      // till that is stuck is worse than a till that says to type it in (`AI-005`).
      if (!(error instanceof NotCropped)) throw error
      setImagesRefused(true)
      return
    }
    setImagesRefused(false)
    setCancelled(false)
    setUnreadable(false)
    setStorageFailed(false)
    setAskedUnder(contextKey)
    let write: { id: string }
    try {
      write = (await enqueue({
        method: 'POST',
        path: '/mira/proposals',
        label: t('mira.askLabel'),
        body: { task, branchId, payload, locale, ...target },
      })) as { id: string }
    } catch {
      // The queue refused to store it, which on a cheap tablet is IndexedDB out of room and with a
      // vision ask is four photographs of body rather than a sale's few hundred bytes. The write is
      // not on the queue and nothing was sent, which is what `enqueue` now guarantees by storing
      // before it registers; before that the rejection travelled out of here unhandled, the
      // request went out and was billed on the next drain, and the panel showed nothing at all.
      setStorageFailed(true)
      return
    }
    setAskWriteId(write.id)
  }

  const confirm = async (proposal: MiraProposal, answer: ComposedAnswer): Promise<void> => {
    const write = (await enqueue({
      method: 'POST',
      path: `/mira/proposals/${proposal.id}/answer`,
      label: t('mira.answerLabel'),
      body: {
        status: answer.status,
        expectedVersion: proposal.version,
        ...(answer.accepted === undefined ? {} : { accepted: answer.accepted }),
      },
    })) as { id: string }
    setAnswered({ writeId: write.id, proposal, answer })
  }

  const reset = (): void => {
    setAskWriteId(null)
    setOutcome(null)
    setUnreadable(false)
    setStorageFailed(false)
    setCancelled(false)
    setAnswered(null)
    setRecorded(false)
    setQuestion('')
    setImages([])
    setImagesRefused(false)
    setAskedUnder(null)
    // The form is not on screen while a proposal is, so the heading is the one thing that is.
    headingRef.current?.focus()
  }

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault()
    void ask()
  }

  const onPanelKey = (event: ReactKeyboardEvent): void => {
    if (event.key !== 'Escape') return
    event.preventDefault()
    close(true)
  }

  const answerWrite = answered
    ? snapshot.writes.find((write) => write.id === answered.writeId)
    : undefined
  const proposal = outcome?.proposal
  // `askedUnder === contextKey` is the `AI-001` guard: these values were read against the customer,
  // order or job that is still open. Anything else open means the handoff is withheld and the line
  // below it says so, rather than prefilling one person's eyes onto another's grid. It is withheld
  // on any change of context and not only on a change of customer, including opening an order for
  // the same person, because the panel cannot tell those apart from `targetType` and `targetId`
  // alone and withholding is the safe way to be wrong.
  const sameContext = askedUnder === null || askedUnder === contextKey
  const takeRx =
    recorded &&
    onTakeRx !== undefined &&
    answered !== null &&
    answered.proposal.task === 'rx_ocr' &&
    answered.answer.status !== 'rejected' &&
    sameContext
      ? rxGridOf(answered.answer)
      : null
  const rxWithheld =
    recorded &&
    answered !== null &&
    answered.proposal.task === 'rx_ocr' &&
    answered.answer.status !== 'rejected' &&
    !sameContext

  const noAnswer =
    !snapshot.online ||
    closedByQuota ||
    outcome?.degradedReason !== undefined ||
    unreadable ||
    askBlocked ||
    storageFailed

  return (
    <div className="lm-mira">
      <button
        type="button"
        className="lm-button lm-button--secondary"
        aria-expanded={open}
        aria-controls={regionId}
        aria-keyshortcuts="Control+K"
        onClick={() => {
          if (open) close(true)
          else openPanel()
        }}
      >
        {t('mira.launch')} <Ltr className="lm-hint">{t('mira.shortcut')}</Ltr>
      </button>

      {open ? (
        <section
          id={regionId}
          className="lm-mira__panel"
          role="region"
          aria-label={t('mira.region')}
          onKeyDown={onPanelKey}
        >
          <div className="lm-mira__bar">
            <h2 ref={headingRef} tabIndex={-1} className="lm-mira__heading">
              {t('mira.heading')}
            </h2>
            <button
              type="button"
              className="lm-button lm-button--secondary"
              onClick={() => {
                close(true)
              }}
            >
              {t('mira.close')}
            </button>
          </div>

          <p className="lm-hint">
            {context.kind === 'customer'
              ? sentence(t, 'mira.context.customer', {
                  name: <span dir="auto">{context.name}</span>,
                })
              : context.kind === 'order'
                ? context.number === null
                  ? t('mira.context.orderNoNumber')
                  : sentence(t, 'mira.context.order', { number: <Ltr>{context.number}</Ltr> })
                : context.kind === 'job'
                  ? sentence(t, 'mira.context.job', { number: <Ltr>{context.number}</Ltr> })
                  : t('mira.context.section', { section: context.label })}
          </p>

          {proposal === undefined ? (
            <form className="lm-mira__form" onSubmit={onSubmit}>
              <p className="lm-hint">{t('mira.noNames')}</p>
              <label className="lm-field lm-field--inline">
                <span className="lm-field__label">{t('mira.task.label')}</span>
                <select
                  className="lm-input"
                  value={task}
                  onChange={(event) => {
                    setTask(event.currentTarget.value as AiTask)
                    // A crop confirmed against a prescription slip is not a payload for a question
                    // about a price list, and the person who chose it chose it for the other task.
                    setImages([])
                    setImagesRefused(false)
                  }}
                >
                  {TASKS.map((entry) => (
                    <option key={entry.task} value={entry.task}>
                      {t(entry.label)}
                    </option>
                  ))}
                </select>
              </label>
              {ASKS_A_QUESTION(task) ? (
                <label className="lm-field">
                  <span className="lm-field__label">{t('mira.input.label')}</span>
                  <input
                    ref={inputRef}
                    className="lm-input"
                    type="text"
                    dir="auto"
                    value={question}
                    onChange={(event) => {
                      setQuestion(event.currentTarget.value)
                    }}
                  />
                </label>
              ) : TAKES_IMAGES(task) ? (
                <MiraImages
                  images={images}
                  onChange={setImages}
                  tools={imageTools}
                  disabled={waiting}
                />
              ) : (
                <>
                  {/* `AI-015` drafts for one of four occasions on one of three channels, and the
                      provider declines a draft payload without both. There is no free text: a
                      question field here would be sent nowhere. */}
                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">{t('mira.occasion.label')}</span>
                    <select
                      ref={occasionRef}
                      className="lm-input"
                      value={occasion}
                      onChange={(event) => {
                        setOccasion(event.currentTarget.value as MiraDraftOccasion)
                      }}
                    >
                      {MIRA_DRAFT_OCCASIONS.map((value) => (
                        <option key={value} value={value}>
                          {t(DRAFT_OCCASION_LABELS[value])}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">{t('mira.channel.label')}</span>
                    <select
                      className="lm-input"
                      value={channel}
                      onChange={(event) => {
                        setChannel(event.currentTarget.value as MiraDraftChannel)
                      }}
                    >
                      {MIRA_DRAFT_CHANNELS.map((value) => (
                        <option key={value} value={value}>
                          {t(DRAFT_CHANNEL_LABELS[value])}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              )}
              <div className="lm-screen__actions">
                <button
                  type="submit"
                  className="lm-button lm-button--primary"
                  aria-disabled={!canAsk}
                >
                  {t('mira.ask')}
                </button>
                {(waiting && askWrite?.status === 'pending') || askBlocked ? (
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    onClick={() => {
                      stopWaiting(true)
                    }}
                  >
                    {t('mira.cancelAsk')}
                  </button>
                ) : null}
              </div>
            </form>
          ) : null}

          <div className="lm-mira__status" role="status">
            {proposal === undefined ? (
              <p className="lm-hint">
                {quotaFailed
                  ? t('mira.quota.unknown')
                  : quotas === null
                    ? t('mira.quota.loading')
                    : quota === undefined
                      ? t('mira.quota.unknown')
                      : quota.state === 'available'
                        ? quota.remaining === undefined
                          ? null
                          : t('mira.quota.available', { count: quota.remaining })
                        : quota.state === 'exhausted'
                          ? t('mira.quota.exhausted')
                          : t('mira.quota.unquoted')}
              </p>
            ) : null}
            {!snapshot.online ? <p>{t('mira.offline')}</p> : null}
            {waiting ? <p>{t('mira.asking')}</p> : null}
            {cancelled ? <p>{t('mira.cancelled')}</p> : null}
            {askWrite?.status === 'blocked' ? (
              <p className="lm-error">
                {t('mira.reason.refused', { reason: askWrite.error?.message ?? '' })}
              </p>
            ) : null}
            {unreadable ? <p>{t('mira.reason.unreadable')}</p> : null}
            {storageFailed ? <p className="lm-error">{t('mira.reason.noRoom')}</p> : null}
            {imagesRefused ? <p className="lm-error">{t('mira.image.needed')}</p> : null}
            {outcome?.degradedReason !== undefined ? (
              <p>{t(REASON_KEYS[outcome.degradedReason])}</p>
            ) : null}
            {noAnswer ? <p>{t('mira.manual')}</p> : null}
          </div>

          {proposal !== undefined && !recorded ? (
            <MiraAcceptance
              task={proposal.task}
              proposed={proposal.proposed}
              model={proposal.model}
              modelVersion={proposal.modelVersion}
              {...(proposal.confidence === undefined ? {} : { confidence: proposal.confidence })}
              busy={answered !== null && answerWrite?.status !== 'blocked'}
              onConfirm={(answer) => {
                void confirm(proposal, answer)
              }}
            />
          ) : null}

          {answerWrite?.status === 'blocked' ? (
            <p className="lm-error" role="alert">
              {t('mira.done.blocked', { reason: answerWrite.error?.message ?? '' })}
            </p>
          ) : null}

          {recorded && answered !== null ? (
            <div className="lm-mira__done" role="status">
              <p>
                {t(
                  answered.answer.status === 'accepted'
                    ? 'mira.done.accepted'
                    : answered.answer.status === 'edited'
                      ? 'mira.done.edited'
                      : 'mira.done.rejected',
                )}
              </p>
              {answered.answer.status === 'rejected' ? null : (
                <p className="lm-hint">{t('mira.done.notSaved')}</p>
              )}
              {rxWithheld ? <p className="lm-hint">{t('mira.done.otherContext')}</p> : null}
              <div className="lm-screen__actions">
                {takeRx === null || onTakeRx === undefined ? null : (
                  <button
                    type="button"
                    className="lm-button lm-button--primary"
                    onClick={() => {
                      onTakeRx(takeRx)
                      close(false)
                    }}
                  >
                    {t('mira.done.takeToRx')}
                  </button>
                )}
                <button type="button" className="lm-button lm-button--secondary" onClick={reset}>
                  {t('mira.again')}
                </button>
              </div>
            </div>
          ) : null}

          {outcome !== null && proposal === undefined ? (
            <button type="button" className="lm-button lm-button--secondary" onClick={reset}>
              {t('mira.again')}
            </button>
          ) : null}

          <p className="lm-hint">{t('mira.closeHint')}</p>
        </section>
      ) : null}
    </div>
  )
}
